import { useState } from 'react'

export default function useQuizModule() {
  const [selectedOptionId, setSelectedOptionId] = useState(null)
  const [selectedOptionIds, setSelectedOptionIds] = useState([])
  const [isAnswered, setIsAnswered] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [isPartiallyCorrect, setIsPartiallyCorrect] = useState(false)
  const [duplicateError, setDuplicateError] = useState(null)
  const [emptyError, setEmptyError] = useState(null)
  const [contextError, setContextError] = useState(null)
  const [commentError, setCommentError] = useState(null)

  const MAX_CONTEXT_LENGTH = 400
  const MAX_OPTION_LENGTH = 400
  const MAX_COMMENT_LENGTH = 400

  const handleContextChange = (input, value) => {
    if (value.length > MAX_CONTEXT_LENGTH) {
      setContextError(`Title cannot exceed ${MAX_CONTEXT_LENGTH} characters`)
      return
    }

    setContextError(null)
    input.onChange(value)
  }

  const handleCommentChange = (input, value) => {
    if (value.length > MAX_COMMENT_LENGTH) {
      setCommentError(`Comment cannot exceed ${MAX_COMMENT_LENGTH} characters`)
      return
    }

    setCommentError(null)
    input.onChange(value)
  }

  const handleQuizTypeChange = (options, input, isMultipleChoice) => {
    if (!isMultipleChoice) {
      const correctOptions = options.filter(opt => opt.correct)

      if (correctOptions.length > 1) {
        const firstCorrectOption = correctOptions[0]
        const updatedOptions = options.map(option => ({
          ...option,
          correct: option.sequenceNumber === firstCorrectOption.sequenceNumber
        }))

        input.onChange(updatedOptions)
      }
    }
  }

  const handleAddOption = (options, onChange) => {
    const maxSequence = options.length > 0 ? Math.max(...options.map(opt => opt.sequenceNumber || 0)) : 0

    const newOption = {
      sequenceNumber: maxSequence + 1,
      text: '',
      correct: false
    }
    onChange([...options, newOption])
  }

  const handleDeleteOption = (options, onChange, optionSequenceNumber) => {
    if (options.length <= 1) return

    const updatedOptions = options.filter(option => option.sequenceNumber !== optionSequenceNumber)
    const deletedOption = options.find(option => option.sequenceNumber === optionSequenceNumber)

    const reorderedOptions = updatedOptions.map(option => {
      if (option.sequenceNumber > optionSequenceNumber) {
        return { ...option, sequenceNumber: option.sequenceNumber - 1 }
      }
      return option
    })

    const isMultipleChoice = options.filter(opt => opt.correct).length > 1
    if (deletedOption?.correct && reorderedOptions.length > 0 && !isMultipleChoice) {
      const sortedOptions = [...reorderedOptions].sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      onChange(sortedOptions.map((option, index) => (index === 0 ? { ...option, correct: true } : option)))
    } else {
      onChange(reorderedOptions)
    }

    if (duplicateError && duplicateError.sequenceNumber === optionSequenceNumber) {
      setDuplicateError(null)
    }

    if (emptyError && emptyError.sequenceNumber === optionSequenceNumber) {
      setEmptyError(null)
    }
  }

  const handleOptionChange = (options, onChange, optionSequenceNumber, newText) => {
    if (newText.length > MAX_OPTION_LENGTH) {
      setEmptyError({
        sequenceNumber: optionSequenceNumber,
        message: `Option text cannot exceed ${MAX_OPTION_LENGTH} characters`
      })
      return
    }

    const updatedOptions = options.map(option =>
      option.sequenceNumber === optionSequenceNumber ? { ...option, text: newText } : option
    )

    if (duplicateError && duplicateError.sequenceNumber === optionSequenceNumber) {
      setDuplicateError(null)
    }

    if (emptyError && emptyError.sequenceNumber === optionSequenceNumber) {
      setEmptyError(null)
    }

    onChange(updatedOptions)
  }

  const handleOptionBlur = (options, onChange, optionSequenceNumber) => {
    const currentOption = options.find(opt => opt.sequenceNumber === optionSequenceNumber)
    if (!currentOption) return

    const currentText = currentOption.text.trim()

    if (currentText === '') {
      setEmptyError({
        sequenceNumber: optionSequenceNumber,
        message: 'Option text cannot be empty'
      })
      return
    }

    const duplicates = options.filter(
      opt => opt.sequenceNumber !== optionSequenceNumber && opt.text.trim().toLowerCase() === currentText.toLowerCase()
    )

    if (duplicates.length > 0) {
      const updatedOptions = options.map(opt =>
        opt.sequenceNumber === optionSequenceNumber ? { ...opt, text: '' } : opt
      )

      setDuplicateError({
        sequenceNumber: optionSequenceNumber,
        message: 'Duplicate option text was cleared'
      })

      onChange(updatedOptions)

      setEmptyError({
        sequenceNumber: optionSequenceNumber,
        message: 'Option text cannot be empty'
      })
    }
  }

  const handleCorrectAnswerChange = (options, onChange, optionSequenceNumber, isMultipleChoice) => {
    if (isMultipleChoice) {
      onChange(
        options.map(option => ({
          ...option,
          correct: option.sequenceNumber === optionSequenceNumber ? !option.correct : option.correct
        }))
      )
    } else {
      onChange(
        options.map(option => ({
          ...option,
          correct: option.sequenceNumber === optionSequenceNumber
        }))
      )
    }
  }

  const handleOptionSelect = (optionId, isCorrectOption) => {
    if (isAnswered) return

    setSelectedOptionId(optionId)
    setIsAnswered(true)
    setIsCorrect(isCorrectOption)
  }

  const handleMultipleOptionSelect = optionId => {
    if (isAnswered) return

    setSelectedOptionIds(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId)
      } else {
        return [...prev, optionId]
      }
    })
  }

  const handleMultipleSubmit = options => {
    const correctOptionIds = options.filter(opt => opt.correct).map(opt => opt.text)
    const correctSelectedCount = selectedOptionIds.filter(id => correctOptionIds.includes(id)).length

    const allCorrectSelected = correctOptionIds.every(id => selectedOptionIds.includes(id))
    const noIncorrectSelected = selectedOptionIds.every(id => correctOptionIds.includes(id))

    setIsAnswered(true)
    setIsCorrect(allCorrectSelected && noIncorrectSelected && selectedOptionIds.length === correctOptionIds.length)
    setIsPartiallyCorrect(
      (correctSelectedCount > 0 && correctSelectedCount < correctOptionIds.length) ||
        (correctSelectedCount > 0 && !noIncorrectSelected)
    )
  }

  const handleReset = () => {
    setSelectedOptionId(null)
    setSelectedOptionIds([])
    setIsAnswered(false)
    setIsCorrect(false)
    setIsPartiallyCorrect(false)
  }

  return {
    selectedOptionId,
    selectedOptionIds,
    isAnswered,
    isCorrect,
    isPartiallyCorrect,
    duplicateError,
    emptyError,
    contextError,
    commentError,
    MAX_CONTEXT_LENGTH,
    MAX_OPTION_LENGTH,
    MAX_COMMENT_LENGTH,
    handleContextChange,
    handleCommentChange,
    handleQuizTypeChange,
    handleAddOption,
    handleDeleteOption,
    handleOptionChange,
    handleOptionBlur,
    handleCorrectAnswerChange,
    handleOptionSelect,
    handleMultipleOptionSelect,
    handleMultipleSubmit,
    handleReset
  }
}
